import type { Variants } from "@braintrust/braintrust-ui-components";
import { trimURL } from "@js/utils/url";

import type { DashboardNavItem } from "../types";

import { EMPLOYER_NAV_ITEMS } from "./employer-navigation";
import { FREELANCER_NAV_ITEMS } from "./freelancer-navigation";

type NavColorVariantType = EnumType<typeof Variants>;

export const findCurrentNavItem = ({
  items,
  exact,
  pathname,
}: {
  items: DashboardNavItem[];
  exact?: boolean;
  pathname: string;
}) => {
  const linkItem = items.find(({ path, getIsActive }) => {
    if (getIsActive) {
      return getIsActive({ pathname });
    }
    const currentPathnameTrimmed = trimURL(pathname);
    const linkPathnameTrimmed = trimURL(path).split("?")[0];
    if (exact) {
      return currentPathnameTrimmed === linkPathnameTrimmed;
    }

    return currentPathnameTrimmed.includes(linkPathnameTrimmed);
  });

  return linkItem;
};

export const getNavMenuColorVariant = (
  currentItem: DashboardNavItem | undefined,
  pathname: string,
): NavColorVariantType => {
  const matchedByPathname = getNavMenuColorForPathname(pathname);

  if (matchedByPathname) {
    return matchedByPathname;
  }

  if (!currentItem) {
    return "nav-default-white";
  }

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (currentItem.label) {
    case EMPLOYER_NAV_ITEMS.MY_TALENT.label:
    case FREELANCER_NAV_ITEMS.HOME.label:
      return "nav-violet";
    case EMPLOYER_NAV_ITEMS.BROWSE_TALENT.label:
    case EMPLOYER_NAV_ITEMS.MY_JOBS.label:
    case FREELANCER_NAV_ITEMS.JOBS.label: {
      return "nav-blue";
    }
    case FREELANCER_NAV_ITEMS.WALLET.label:
    case EMPLOYER_NAV_ITEMS.INVOICES.label:
      return "nav-green";
    case FREELANCER_NAV_ITEMS.EARN.label:
    default:
      return "nav-default-white";
  }
};

type PathnameColorVariantsType = {
  regexp: RegExp;
  variant: NavColorVariantType;
};

const pathnameColorVariants: PathnameColorVariantsType[] = [
  { regexp: /^(\/talent\/\d+)/, variant: "nav-violet" },
  { regexp: /\/jobs\/\d+\/offers\/\d+\/accepted\/?$/, variant: "nav-green" },
  { regexp: /^(\/talent\/?$|\/jobs\/?|\/employers\/\d+)/, variant: "nav-blue" },
  { regexp: /^(\/messages\/?)/, variant: "nav-orange" },
  { regexp: /^\/employer\/invoices\/invoice_batches\/?/, variant: "nav-green" },
  { regexp: /^\/admin\/ecp/, variant: "nav-violet" },
  { regexp: /^\/rosters/, variant: "nav-violet" },
];
const getNavMenuColorForPathname = (
  pathname: string,
): NavColorVariantType | undefined => {
  const matchingVariantData = pathnameColorVariants.find(({ regexp }) =>
    regexp.test(pathname),
  );

  return matchingVariantData?.variant;
};

export const checkIfAnySubLinkIsActive = (
  subLinks: DashboardNavItem[],
  pathname: string,
) => {
  const subLinksWithNestedSubLinks = subLinks.filter(
    (subLink) => subLink.subLinks,
  ) as Required<DashboardNavItem>[];
  let flag = false;
  if (subLinksWithNestedSubLinks.length) {
    subLinksWithNestedSubLinks.forEach((subLink) => {
      flag =
        flag || !!findCurrentNavItem({ items: subLink.subLinks, pathname });
    });
  }
  return flag || !!findCurrentNavItem({ items: subLinks, pathname });
};
